/*
    This file is part of Universal Sequences.
    Universal Sequences is free software: you can redistribute it and/or modif
    it under the terms of the GNU General Public License as published by
    the Free Software Foundation, either version 3 of the License, or
    (at your option) any later version.

    Universal Sequences is distributed in the hope that it will be useful,
    but WITHOUT ANY WARRANTY; without even the implied warranty of
    MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
    GNU General Public License for more details.

    You should have received a copy of the GNU General Public License
    along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import Tone from 'tone';
import ClockSync from 'clock/ClockSync.js';
import Midi from 'Midi';
import {MAP, DELETE, OFF} from 'constants/midi-map-modes.js';
import Resampler from 'track/Resampler.js';
import {MULTI_SYNTH} from 'constants/instrument-types.js';
import MetronomeTrack from 'track/MetronomeTrack.js';
import EnvelopesTrack from 'track/EnvelopesTrack.js';
import Scheduling from './Scheduling.js';
import LFOsTrack from 'track/LFOsTrack.js';
import PatternSwitchEvent from 'session/PatternSwitchEvent.js';
import {PALETTE_EFFECT} from 'effects/live/constants.js';
import {sortTracksByDependencies} from 'effects/midi/scripting/dependencies.js';
import {PIANO_ROLL, WAVEFORM} from 'constants/arrangement-modes.js';
import {publishRoll, publishTrigger} from 'messaging/messages.js';
import {clearQueue} from 'messaging/queue.js';
import Scenes from 'scenes/Scenes.js';
import {mod} from 'utils/mod.js';
import {FORWARD, REVERSE, FORWARD_REVERSE} from 'constants/playhead-directions.js';
import {getTriggerTimes} from 'keyboard/TriggerTimeHelper.js';
import DreamSampler from 'instruments/DreamSampler.js';
import ParameterContext from 'effects/ParameterContext.js';
import Auditionable from "engine/Auditionable.js";
import StepData from 'StepData';
import {PATTERN_SWITCH} from 'session/constants.js';
import {getStepFromPosition} from 'components/arrangement/getStepFromPosition.js';
import {triggerTracks} from './triggerTracks.js';
import Resolution from './Resolution.js';
import SequenceRollEvent from 'session/SequenceRollEvent.js';
import Rolling from 'keyboard/Rolling.js';
import Effects from 'Effects.js';
import axios from 'axios';
import Track from 'Track.js';
import SequencerEngine from 'SequencerEngine.js';
import Grouper from './Grouper.js';
import startaudiocontext from 'startaudiocontext';
import crop from 'Crop.js';
import AutomationScheduler from './AutomationScheduler.js';
import Pattern from 'Pattern.js';
import Sends from 'Sends.js';
import Sampler from 'Sampler.js';
import KeyboardHelper from 'keyboard/KeyboardHelper.js';
import {
    setActionMidiMapSelection,
    setArrangementSelection,
    setTrackRolling,
    setResampleMaster,
    setTracksToResample,
    setIsRecording,
    setCurrentPattern,
    setCurrentStep,
    setCurrentAndGlobalSteps,
    setCurrentGlobalStep,
    setIsPlaying,
    setIsPlayingMix,
    setBPM,
    setPlayScheduled,
    trackRecording,
    trackPreparingToRecord,
    setArrangementLooping,
    setTrackToFreeze,
    setEffectParameterValues,
    setArrangementCursorStep
} from 'actions/index.js';
import {deleteSteps} from 'operations/actions.js';
import {recordStep} from 'operations/actions.js';
import {deleteStep} from 'operations/actions.js';
import {INSTRUMENT} from 'constants/track-types.js';
import LFOTrack from 'track/LFOTrack.js';
import TrackRollAction from 'midi/TrackRollAction.js';
import SequenceRollAction from 'midi/SequenceRollAction.js';
import {AbstractParameter} from 'parameters/AbstractParameter.js';
import {AbstractAudioParamParameter} from 'parameters/AbstractAudioParamParameter.js';
import {SLAVE} from 'constants/clock-types.js';
import {SEQUENCE_ROLL} from 'session/constants.js';
import {SEQUENCER_VIEW} from 'constants/view-modes.js';
import {ARRANGEMENT_VIEW} from 'constants/view-modes.js';
import Rollable from './Rollable.js';
import {compose} from "utils/compose.js"

const defaultDuration = 1;
const SAMPLE_RATE = 44300.0;
const STABLE_LOOK_AHEAD = 80; // 80 ms

const TIME_16N = new Tone.Time('16n');
const TIME_32N = new Tone.Time('32n');

// A sub-class of SequencerEngine that sequences sounds via Tone.js
class PlayableSequencerEngine extends
compose(
    Auditionable,
    Rollable,
    Scheduling,
    Grouper,
    AutomationScheduler)
(SequencerEngine) {
    constructor(store, transport, aubio, stereoOnly, noMetronome, isOffline) {
        super(store, transport, aubio, stereoOnly, isOffline);

        // keeps track of the last time a step was made
        this.currentStepTime = null;
        this.previousStepTime = null;
        this.lastTrackStep = undefined;

        // tracksToRecord keeps track of which tracks we want to record
        // on the next "step 0" event
        this.tracksToRecord = {};

        // tracksRecording keeps track of which tracks are currently being recorded
        this.tracksRecording = {};
        
        this.tracksToStopRecording = {};

        // rolling manager helps deal with the "rolling" feature of the sqeuencer
        this.rollingManager = new Rolling();

        this.mixButtons = [];

        this.nextTimeScheduled = undefined;
        this.nextStepScheduled = undefined;
        this.startedTransportExplicitly = false;

        this.patternSwitchScheduled = null;
        this.stepNumberOffset = 0;

        // TODO: get rid of these variables since it's in redux and is
        // conflicting
        this.isRecording = false;
        this.playing = false;

        // when true we "beat repeat" the current portion of
        // the sequence
        this.sequenceRolling = false;

        this.resampler = undefined;

        this.automationStep = 0;

        this.bpmParameter= new AbstractAudioParamParameter(
            'BPM', this.transport.bpm,
            (o, x, automation) => this.onNewBpm(x, automation),
            {
                min: 1,
                max: 500,
                unit: '',
            });

        this.bpmParameter.setContext(new ParameterContext().forSequencerEngine(this));
        if (!window.explore) {
            this.bpmParameter.set(generateRandomBPM());
        } else {
            this.bpmParameter.set(Tone.Transport.bpm.value);
        }

        this.swingParameter= new AbstractParameter(
            'Swing', this,
            (o, x) => {
                this.onNewSwing(x);
            },
            {
                min: 50,
                max: 75,
                unit: '',
                defaultValue: 50
            });

        this.parameters = [];
        this.registerParameter(this.bpmParameter);
        this.registerParameter(this.swingParameter);

        this.latency = undefined;
        this.clockSync = new ClockSync(this);

        this.masterResampler = new Resampler(this, this.groups.channelMixer.stereoMerger, true);

        this.transpose = 0;
        this.autoResampleMode = false;

        this.metronome = !noMetronome && new MetronomeTrack(
            this,
            this.mixSession,
            1,
            () => []);

        this.deleting = false;
        this.scenes = new Scenes(this);
        this.parametersToUpdate = {};

        this.initAudition();
    }

    registerParameter(parameter) {
        this.parameters.push(parameter);
        parameter.setContext(new ParameterContext().forSequencerEngine(this));
        let ret = parameter.setStore(this.store);
        this.dispatch(
            setEffectParameterValues(ret));
    }

    async startPlaySequence(time, syncing) {
        this.playSequence.cancel();
        this.playSequence.start(time);
        if (this.getSamplerPlaySequence) {
            this.getSamplerPlaySequence().start(time);
        }
    }


    schedulePlay() {
        if (!this.playing && this.clockSync.connected &&
           this.clockSync.clockType === SLAVE) {
            this.store.dispatch(setPlayScheduled(true));
        } else {
            this.play();
        }
    }

    // toggles playing of the sequence
    play(time, syncing=false) {
        //if (this.context.state === "suspended") {
        //this.context._context.resume();
        //}
        if (this.keyboardHelper) {
            this.keyboardHelper.release = undefined;
        }
        this.playTimestamp = new Date().getTime();
        this.playing = !this.playing;
        this.latency = undefined;

        this.firstStep = true;
        this.trackStep = this.isLooping ? this.loopStart : this.startStep;
        if (this.state.keyboard.cursor &&
            this.isShowingPianoRoll()) {
            this.trackStep = Track.baseResolution.transformStepAtResolution2(
                this.state.keyboard.cursor,
                this.displayResolution);
        }
        this.currentStep = 0;
        this.previousStep = 0;
        this.previousStepTime = undefined;
        this.currentStepTime = undefined;

        if (this.playing) {
            if (!syncing && !this.hasAtleastOneSequencerEnginePlaying()) {
                // this is the first sequence to be played (we
                // aren't syncing with another sequences bpm), so
                // we update the bpm to this sequences' own, saved bpm
                this.updateBpm(this.bpm);
            }

            this.startPlaySequence(time, syncing);

            if (syncing || !this.hasAtleastOneSequencerEnginePlaying()) {
                this.startedTransportExplicitly = true;
                this.transport.start('+0.00001');
                if (this.midi) {
                    this.midi.onPlay();
                }
            }

            this.restartAllMixes();

            // start any LFOs that need to be started (project-wide LFOs,
            // effect LFOs, and synth LFOs)
            for (let sub of this.subSequences) {
                sub.startZObjects();
                sub.midiEffects.clear();
                // this.subSequences[i].lfosTrack.start();
                sub.tracks.forEach(track => track.restart());
                sub.tracks.forEach(track => track.start());
            }

            // update redux so the UI knows we are "playing"
            this.store.dispatch(setIsPlaying(this.getSequenceId(), true));
        } else {
            // otherwise we are stopping
            this.stop();
        }

        this.store.dispatch(setPlayScheduled(false));

        this.sendPlayChange(this.playing);
    }

    restartAllMixes(isLooping=this.isLooping) {
        this.mixes.forEach(
            mix => mix.restart(isLooping));

        this.setSequenceRolling(false, undefined, true);
        for (let i in this.subSequences ){
            for (let j in this.subSequences[i].tracks) {
                let track = this.subSequences[i].tracks[j];
                track.patternRollStepNumber = undefined;
            }
        }
        this.schedulePatternBufferClear = false;
    }

    stop() {
        this.restartAllMixes();
        this.latency = undefined;
        this.playing = false;
        let trackStepReal = this.trackStepReal;
        this.trackStepReal = 0;

        this.higherOrderSequencer.stop();
        this.playSequence.stop();
        this.startedTransportExplicitly = false;

        this.clearScheduledAutomations();

        if (!this.hasMultipleSequencerEnginesPlaying()) {
            // this is the last sequence to be stopped, so we stop the
            // entire transport
            this.transport.stop('+0.01');
            if (this.midi) {
                this.midi.onStop();
            }
        }
        
        // stop any LFOs needed
        for (let i in this.subSequences) {
            this.subSequences[i].lfosTrack.stop();
            this.subSequences[i].stopZObjects();
        }

        // stop any effects w lfo
        this.tracks.forEach(
            track => track.effects.filter(effect => effect.syncable())
                .forEach(effect => effect.stop()));

        clearQueue();

        // stop all midi effects (they might need to clear state)
        for (let sub of this.subSequences) {
            sub.midiEffects.stop();
            sub.tracks.forEach(
                t => {
                    // TODO: really need to move this to a track.stop() function
                    if (t.stepGroups.length > 0) {
                        // then we need to unset the transformation
                        t.pattern.transformStepBuckets = [];
                        t.pattern.isTransformed = false;
                        if (t.subSequence === this.currentSubSeuqence) {
                            t.updateSteps();
                        }
                    }

                    t.resetAnyPLockedParameters();
                    t.midiEffects.stop();
                    t.notesTriggered = {};

                    if (!t.isFrozen && t.instrument && t.instrument.releaseAll) {
                        t.instrument.releaseAll();
                    }

                    // for instrument racks
                    if (t.tracks) {
                        t.tracks.forEach(
                            ({track}) => track.midiEffects.stop());
                    }
                });
        }
        
        // update redux so UI knows we are no longer playing
        this.store.dispatch(setIsPlaying(this.getSequenceId(), false));
        if (this.state.sequencer.viewMode !== ARRANGEMENT_VIEW) {
            this.store.dispatch(setIsPlayingMix(false));
        }
        // this.store.dispatch(setCurrentGlobalStep(this.getSequenceId(), 0));

        if (this.clockSync) {
            this.clockSync.onStop();
        }

        for (let i in this.subSequences) {
            this.subSequences[i].tracks.forEach(
                t => {
                    t.chokes.chokingDuration = 0;
                    t.chokes.liveRelease();
                });
        }

        this.resetAutomationParameters();
        this.playSequence.cancel();
        this.loadNewSequence();

        this.handleStopMode();
    }

    get startStep() {
        let viewMode = this.state.sequencer.viewMode;
        return this.state.arrangement.cursorStep  === undefined ||
            (viewMode === SEQUENCER_VIEW && !this.isLooping)  ?
            0 : 
            this.state.arrangement.cursorStep;
    }

    // called when user uses the sequencer clock to seek to
    // particular section of a track.
    seekToTrackStep(trackStep, switchPattern=true) {
        if (this.isLooping) {
            this.looping = false;
        }
        
        this.trackStep = trackStep;
        this.store.dispatch(setCurrentGlobalStep(this.getSequenceId(), trackStep));
        this.mixes.forEach(mix => mix.seekToTrackStep(trackStep));
        this.stepNumberOffset = 0;

        if (trackStep == 0 && switchPattern) {
            if (!this.isShowingPianoRoll()) {
                this.switchToPatternHelper(0);
            }
        }

        this.subSequences.forEach(
            sub => sub.tracks.forEach(t => t.scheduleTrackPatternChangeIfAny(this.trackStep, true)));
    }

    // creates a new blank sequence
    loadNewSequence() {
        if (this.playSequence) {
            this.playSequence.dispose();
        }
        this.playSequence = this.getPlaySequence();
    }

    async sleep(time) {
        return new Promise((resolve, reject) => {
            setTimeout(() => 
                       resolve(time), time);
        });
    }

    get isLooping() {
        return this.state.arrangement.looping;
    }

    set looping(x) {
        this.store.dispatch(setArrangementLooping(x));
    }

   get loopStart() {
        return this.state.arrangement.selection.start;
    }

    get loopEnd() {
        return this.state.arrangement.selection.end;
    }

    startNewLoop(bars=1) {
        let steps = bars*32;
        let trackStep = this.trackStep;
        let remainder = mod(trackStep + (this.stepNumberOffset || 0), steps);
        trackStep -= remainder;

        while (trackStep < 0) {
            trackStep += 16;
        }

        this.dispatch(setArrangementSelection(
                trackStep,
                trackStep+steps
            ));
        this.looping = true;
    }


    // schedules the events for the sequencer, where the
    // real meat and potatoes are...
    // returns a Tone.Sequence object that can be started/stopped etc
    getPlaySequence() {
        var steps = Array(16).fill(1).map((v, i) => { return i; });

        var playSequence = new Tone.Sequence((time, currentStep) => {
            this.scheduleStep(time, currentStep);
       }, steps, '32n');
        playSequence.loop = true;
        return playSequence;
    }

    getLoopedStep(newTrackStep) {
        let og = newTrackStep;
        if (this.isLooping) { //&& this.state.mix.isPlayingMix) {
            let diff = newTrackStep - this.loopStart;
            let loopLength = this.loopEnd - this.loopStart;
            let modded = diff % loopLength;
            newTrackStep = this.loopStart + modded;
            return newTrackStep;
        }
        return newTrackStep;
    }

    handleLatency(time) {
        if (this.latency === undefined && !this.clockSync.initiator) {
            this.timeToCompare = this.timeToCompare - new Date().getTime();
            this.latency = this.timeToCompare - this.getMilliseconds(time);
            this.latency += TIME_32N.toMilliseconds();
            let latencyFromPlay = new Date().getTime() - this.playTimestamp;
            this.latency -= latencyFromPlay / 2;
        }
        if (this.latency) {
            time+=this.latency / 1000.0;
        }
        time += this.state.sequencer.latency / 1000.0;
        return time;
    }

    // handles all the necessary actions to be taken when
    // we are "playing" a mix. this includes automations,
    // pattern changes, erasing of events, etc.
    handleMixSessionStep(time) {
        // check if we are playing a full song's arrangement
        if (this.state.mix.isPlayingMix) {
            // check if we reached the loop start
            if (this.isLooping && this.trackStep === this.loopStart) {
                this.restartAllMixes(true);
            }

            // do we have any pattern switches?
            for (let i in this.subSequences) {
                let subSequence = this.subSequences[i];
                let patternSwitch = this.subSequences[i].mix.getValue(
                    PATTERN_SWITCH, this.automationStep);

                if (patternSwitch !== undefined &&
                    patternSwitch !== this.getCurrentPattern(subSequence)) {
                    this.switchToPattern(patternSwitch, true, subSequence, undefined, time);
                }
            }

            // check if we need to set the sequence rolling here, based on the mix
            let isSequenceRolling = this.isSequenceRollingAtStep(this.automationStep);
            if (isSequenceRolling !== undefined &&
                isSequenceRolling !== this.isSequenceRolling) {
                this.setSequenceRolling(isSequenceRolling, undefined, true);
            }
            
            try {
                // if we are playing a mix we should schedule any automation 
                // events that should occur on this step
                this.mixes.forEach(mix => mix.scheduleNext(this.trackStep, time));
            } catch (err) {
                console.log('caught err while scheduling automation = ');
                console.log(err);
            }
        } 

        // schedule all automations for patterns
    }

    scheduleParametersToRecord(currentStep) {
        if (!this.isRecording) {
            return;
        }

        let parameters = this.state.tracks.parametersToRecord;
        for (let i in parameters) {
            let parameter = parameters[i];
            if (!parameter || !parameter.context) {
                continue;
            }
            if (parameter.context.track) {
                let track = parameter.context.track;
                let pattern = track.pattern;
                let patternStep = currentStep % track.length;
                let bucket = pattern.stepBuckets[patternStep];
                for (let j in bucket.steps) {
                    let step = bucket.steps[j];
                    if (!step.on) {
                        continue;
                    }
                    let stepParameters = step.parameters
                        .filter(param => param.name === parameter.name);
                    stepParameters.forEach(param => param.set(parameter.get()));
                }
                
            }
        }
    }

    // midi tracks and audio tracks allow recording of audio
    // from the "input" (i.e. a microphone, soundflower, interface)
    // this recording usually starts at step 0 of the track
    scheduleAnyTrackRecording(track, time) {
        let state = this.store.getState();

        // check if we should record any audio coming from midi tracks's
        // since it's step 0
        if (this.tracksToRecord[track.trackNumber]) {
            // we need to start recording this track
            track.startRecording(time);
            this.store.dispatch(trackRecording(track.trackNumber, true));
            this.tracksRecording[track.trackNumber] = true;
            delete this.tracksToRecord[track.trackNumber];
        } else if (this.tracksToStopRecording[track.trackNumber]) {
            // we need to stop recording this track
            track.stopRecording(time);
            this.store.dispatch(trackRecording(track.trackNumber, false));
            delete this.tracksRecording[track.trackNumber];
            delete this.tracksToStopRecording[track.trackNumber];
        }
    }

    scheduleAnyMasterResampling(time) {
       let state = this.store.getState();
        // check if we're already resampling this track
        if (this.resampler !== undefined) {
            this.resampler.resamplingDurationElapsed++;

            // check if enough time has elapsed such that we should
            // stop resampling
            if (this.resampler.resamplingDurationElapsed ==
                state.tracks.resampleDuration) {
                // so we stop resampling
                this.resampler.stop(time);
                this.resampler = undefined;
            }
        }
        
        if (state.sequencer.resampleMaster) {
            // check if we should resample this track
            this.store.dispatch(setResampleMaster(false));
            this.resampler = this.masterResampler; 
            this.resampler.start(time);
        }
    }

    // schedules the starting or stopping of the resampling
    // of a track, if needed
    scheduleAnyTrackResampling(track, time)  {
       let state = this.store.getState();

        if (track.subSequence !== this.currentSubSequence) {
            return;
        }

        // check if we're already resampling this track
        if (track.resampler !== undefined) {
            track.resampler.resamplingDurationElapsed++;

            // check if enough time has elapsed such that we should
            // stop resampling
            if (track.resampler.resamplingDurationElapsed ==
                state.tracks.resampleDuration) {
                // so we stop resampling
                track.resampler.stop(time);
                track.resampler = undefined;
            }
        }
        
        // check if we should resample this track
        let tracksToResample = state.tracks.tracksToResample;
        let indexOfTrackToResample = tracksToResample.indexOf(track.trackNumber);
        if (indexOfTrackToResample > -1) {
            tracksToResample.splice(indexOfTrackToResample, 1);
            this.store.dispatch(setTracksToResample(tracksToResample));
            track.resampler = track.masterResampler;
            track.resampler.start(time);
        }

        if (state.tracks.trackToFreeze &&
            state.tracks.trackToFreeze.trackNumber === track.trackNumber &&
            state.tracks.trackToFreeze.subSequenceId === track.subSequence.id) {
            track.resampler = track.masterResampler;
            track.resampler.start(time, track, true);
            this.store.dispatch(setTrackToFreeze(undefined));
        }
        
    }

    scheduleAnyFrozenTrackPlaying(track, time) {
    }

    // Midi tracks can contain recorded audio, and when the step is 0
    // we need to play any recordings that need to be played
    scheduleAnyAudioTrackPlaying(track, time) {
        if (track.getTrackType() === 'MIDI' &&
            Object.keys(track.samplers).length > 0) {
            let stepData = new StepData();
            stepData.loadDefaults();
            stepData.attack = 80;
            stepData.release = TIME_16N.toMilliseconds() / 4;
            track.triggerSamples(stepData, time);
        }
    }

    scheduleTrackRolling(track, currentStep, time) {
        if (!track.subSequence.isTrack) {
            currentStep = track.getPatternStep(currentStep);
        }
        let millisecondsUntilStep = this.getMilliseconds(time);
        
        // we set this to timeout of millisecondsUntilStep - 10 ms so that
        // the user can cancel a roll up to 10 ms before the next step before
        // that step includes it
        let resolution = track.getFactoredResolution();

        setTimeout(() => {
            let triggers = this.rollingManager.getTriggers(currentStep, resolution, track);
            
            if (triggers.filter(x => x.trackSlice.trackNumber === track.trackNumber).length > 0) {
                track.onNewStep();
            } else {
                return;
            }
            triggers.forEach(trigger => {
                track = this.tracks[trigger.trackSlice.trackNumber];
                if (track === undefined) {
                    return;
                }
                let stepData = new StepData();
                stepData.slice = trigger.trackSlice.sliceNumber;
                stepData.velocity = track.trackData.velocity;
                stepData.transpose = trigger.trackSlice.transpose === undefined ?
                    track.trackData.transpose : trigger.trackSlice.transpose + track.trackData.transpose;
                stepData = track.getStepDataForStep(stepData);
                let newTime = new Tone.Time(time) + trigger.time;
                stepData.track = track;
                track.midiEffects.input(
                    stepData, newTime, currentStep);
                if (this.isRecording || (track.subSequence.isTrack && this.isRecordingMix)) {
                   track.recordHit(
                        trigger.step, resolution, stepData.slice,
                       null, stepData.transpose, stepData.velocity, this.keyboardHelper.release);
                }
            });
            triggers = track.midiEffects.output(time, currentStep);
            let tracks = [track];
            let tracksToTrigger = {};
            tracksToTrigger[track.trackNumber] = triggers;
            triggerTracks(this, tracks, tracksToTrigger, time, currentStep);
            track.onStepEnded(time, currentStep);
        }, millisecondsUntilStep - 20);
    }

    // schedules any sample triggers currently being triggered by 
    // the user with "roll" enabled.
    scheduleSamplesRolling(currentStep, time) {
        this.tracks
            .filter(track => track.rolling)
            .forEach(
                track =>
                    this.scheduleTrackRolling(track, currentStep, time));
    }

    missedNextScheduledStep() {
        return Tone.Master.context.currentTime <= this.nextTimeScheduled;
    }

    // schedules a sampler to trigger on the very next step this 
    // is used in the "rolling" mode, when the user hits a trigger and we 
    // want it to start triggering on the very next step.
    scheduleNext(trackNumber, sliceNumber, transpose) {
        let track = this.tracks[trackNumber]
        if (track.getTrackType() === INSTRUMENT) {
            return;
        }

        // we use the variable nextStepScheduled, which keeps track of the time
        // of the next step that has been already scheduled to be triggered
        let triggerTimes = this.rollingManager.getTriggerTimes(
            this.nextStepScheduled, this.resolution, track);

        let resolution = track.getFactoredResolution();
        // go through the trigger times for this roll event
        triggerTimes.forEach(obj => {
            let stepData = new StepData();
            stepData.slice = sliceNumber;
            stepData.transpose = transpose === undefined ?
                track.trackData.transpose :
                transpose + track.trackData.transpose;
            stepData = track.getStepDataForStep(stepData);

            track.trigger(stepData, this.nextTimeScheduled + obj.time);

            if (this.isRecording) {
                track.recordHit(
                    obj.step,
                    resolution,
                    sliceNumber,
                    stepData.duration,
                    stepData.transpose);
                track.updateSteps();
            }
        });
    }

    getCurrentStepTime() {
        let timeToStep = this.getMilliseconds(this.latestStepTime);
        let previousStepTime = 
            new Tone.Time(Tone.Master.context.currentTime).toMilliseconds() - 
            new Tone.Time(this.previousStepTime).toMilliseconds(); 

        if (previousStepTime < 0 || (timeToStep > 0 && timeToStep > previousStepTime)) {
            return this.previousStepTime;
        }
        return this.latestStepTime;
    }

    // gets the current step at the given resolution
    getCurrentStep(resolution, track) {
        // latest vs previous step time
        let timeToStep = 
            Math.abs(new Tone.Time(Tone.Master.context.currentTime) - 
                     new Tone.Time(this.latestAbsoluteTime)); 
        let previousStepTime = 
            Math.abs(new Tone.Time(Tone.Master.context.currentTime) - 
                     new Tone.Time(this.previousAbsoluteTime)); 

        let length = undefined;
        if (track) {
            if (track.subSequence.isTrack) {
                length = 1000000;
            } else {
                length = track.pattern.length();
            }
        } else {
            length = this.length;
        }
        let trackStep = this.lastTrackStep; 
        let currentStep = trackStep % length;
        if ((timeToStep > 0 && timeToStep > previousStepTime)) {
            // currentStep = this.previousStep;
        }
        resolution = resolution || Track.baseResolution;
        var current = Math.floor(
            currentStep /
                (Track.baseResolution.numSteps() / resolution.numSteps()));
        return current;
    }

    // returns the milliseconds until a given time, using
    // the audio context's current time
    getMilliseconds(time) {
        return new Tone.Time(time).toMilliseconds() -
            new Tone.Time(Tone.Master.context.currentTime).toMilliseconds();;
    }

    // tells us that we want to record a given track
    // at the next step 0.
    recordTrack(trackNumber) {
        this.tracksToRecord[trackNumber] = true;
    }

    stopRecordingTrack(trackNumber) {
        this.tracksToStopRecording[trackNumber] = true;
    }


    // returns whether we are currently recording a given track
    isTrackRecording(trackNumber) {
        return this.tracksRecording[trackNumber];
    }

    doubleTrackLength(trackNumber) {
        this.tracks[trackNumber].doubleTrackLength();
        this.transport.loopStart = '0:0:0';
        this.transport.loopEnd = this.length / 32 + ":0:0"; 
    }

    halfTrackLength(trackNumber) {
        this.tracks[trackNumber].halfTrackLength();

        this.transport.loopStart = '0:0:0';
        this.transport.loopEnd = this.length / 32 + ":0:0"; 
    }

    get isRecordingMix() {
        return this.store.getState().mix.isRecordingMix 
    }

    get lfos() {
        return this.lfosTrack.lfos;
    }

    get envelopesTrack() {
        return this.currentSubSequence.envelopesTrack;
    }

    get lfosTrack() {
        return this.currentSubSequence.lfosTrack;
    }

    get envelopes() {
        return this.envelopesTrack.envelopes.map(
            envTrack =>
                envTrack.envelope);
    }

    async onNewBpm(bpm, isAutomation=false) {
        if (bpm !== undefined && bpm < 500 && bpm >= 1) {
            if (!isAutomation) {
                this.transport.bpm.value = bpm;
            }
            this.store.dispatch(setBPM(bpm));
            for (let i in this.subSequences) {
                this.subSequences[i].lfosTrack.onBPMUpdated(); 
            }
            this.tracks.forEach(track => track.onBPMUpdated());
            if (this.clockSync) {
                this.clockSync.onBPMChanged(bpm);
            }
       }
    }

    get bpm() {
        return this.bpmParameter.get();
    }

    set bpm(b) {
        this.bpmParameter.set(b, false);
    }

    get swing() {
        return this.swingParameter.get();
    }

    set swing(s) {
        this.swingParameter.set(s);
    }

    async updateBpm(bpm, syncing=false) {
        this.bpm = bpm;
        if (!syncing) {
            for (let i in this.subSequences) {
                this.subSequences[i].lfosTrack.onBPMUpdated(); 
            }
            this.tracks.forEach(track => track.onBPMUpdated());
        }
    }

    async onNewSwing(swingPercentage) {
        var swing = (swingPercentage - 50.0) / 50.0;
        this.transport.swing = swing;
    }

    updateSwing(swingPercentage) {
        this.swing = swingPercentage;
    }

    recording(recording) {
        // tell midi steps it is recording
        this.store.dispatch(setIsRecording(recording));

        this.isRecording = recording;

        this.tracks
            .filter(
                track => track.getTrackType() === 'MIDI')
            .map(
                track => track.recording(recording));
    }

    connectToKeyboardEngine(keyboardEngine) {
        this.keyboardEngine = keyboardEngine;
        this.keyboardHelper = new KeyboardHelper(this, keyboardEngine);
        this.midi = new Midi(keyboardEngine, this.keyboardHelper, (bpm) => this.updateBpm(bpm));
        this.midi.setStartCallback(() => this.play());
        this.midi.setStopCallback(() => this.stop());
    }

    // returns whether there are multiple sequencer engines
    // playing. this happens when we are in the "user" view
    // playing mulitple songs synced together
    hasMultipleSequencerEnginesPlaying() {
        let isPlaying = this.store.getState().sequencer.isPlaying;
        let count = Object.keys(isPlaying).filter(
            id => isPlaying[id]).length;
        return count > 1;
    }

    hasAtleastOneSequencerEnginePlaying() {
        let isPlaying = this.store.getState().sequencer.isPlaying;
        let count = Object.keys(isPlaying).filter(
            id => isPlaying[id]).length;
        return count > 0;
    }

    // getter for redux state
    get state() {
        return this.store.getState();
    }

    get isLoading() {
        let loadingSequence = this.state.sequencer.loadingSequence;

        return Object.keys(loadingSequence).some(
            (s) => loadingSequence[s] === true);
    }

    sustain(value) {
        // get enabled tracks
        let toggledTracks = this.tracks.filter(
            track =>
                track.isEnabled);

        for (let track of toggledTracks) {
            let ampEnvelope = undefined;
            if (track.getTrackType() === INSTRUMENT) {
                let instrument = track.instrument;
                if (instrument.getInstrumentType() === MULTI_SYNTH) {
                    ampEnvelope = instrument.ampEnvelope;
                } else {
                    ampEnvelope = instrument;
                }
            } else {
                continue;
            }
            let kbd = this.keyboardHelper;
            let release = 0;
            if (value === 127) {
                // find any step that is being recorded and set the release parameter there
                ampEnvelope.releaseParameter.set(2500, true);
                release = 2500;
                
            } else {
                ampEnvelope.releaseParameter.set(100, true);
                release = 100;
            }

            kbd.release = release;

            // for the triggers that are already under way
            for (let t in kbd.recordedTriggers) {
                let trg = kbd.recordedTriggers[t];
                if (trg.recordedTriggers) {
                    trg.recordedTriggers.forEach(
                        x => x.release = release);
                }
            }
        }
    }

    isShowingPianoRoll() {
        return this.state.sequencer.showPianoRoll && this.state.arrangement.bottomMode === PIANO_ROLL;
    }

    isAnyTrackEnabled() {
        for (let sub of this.subSequences) {
            if (sub.tracks.some(track => track.isEnabled)) {
                return true;
            }
        }
        return false;
    }

    sendPlayChange(playing) {
        this.subSequences.forEach(
            sub => {
                sub.findNodeByName("mixbuttons").forEach(
                    x => {
                        x.effect.sendMessage("playing", playing ? 1 : 0);
                    });
            });
    }

    handleStopMode() {
        this.subSequences.forEach(
            sub => {
                sub.findNodeByName("mixbuttons").forEach(
                    x => {
                        x.effect.handleStop();
                    });
            });
    }
}

const generateRandomBPM = () => {
    return 60 + Math.floor(Math.random()*120);
}
 

export default PlayableSequencerEngine;
